import React, { ReactNode } from 'react'

import styled from 'styled-components'

import {
  Body2Regular,
  Body3,
  StyledLinkLarge,
  TitleH2,
  TitleSmall,
  TitleXL
} from 'src/components/blocks/Typography'
import { Container } from 'src/components/partials/Container'
import { ResponsiveSectionWrapper } from 'src/components/partials/Section'
import { Breakpoints } from 'src/utilities/Breakpoints'
import { BrandColors, BrandTokens } from 'src/utilities/Colors'
import { IQuoteProps } from 'src/utilities/Interfaces'
import FaIcon from '../partials/FaIcon'

const QuoteCardWrapper = styled.div`
  padding: 3rem 2rem;
  background-color: ${BrandColors.white};
  max-width: 762px;
  box-sizing: border-box;
  margin: 0 auto;
  text-align: center;
  box-shadow: 0 14px 25px -5px rgba(0, 0, 0, 0.2),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border-radius: 9px;
  ${TitleXL} {
    margin-top: 3rem;
  }
  ${TitleSmall} {
    font-style: normal;
    margin-top: 1rem;
  }
  .logo {
    margin-top: 3rem;
  }
  @media (min-width: ${Breakpoints.sm}px) {
    padding: 4rem 6.5rem;
    .logo {
      margin-top: 4rem;
    }
  }
`

export const QuoteCard = ({ quote }: { quote: IQuoteProps }) => (
  <QuoteCardWrapper>
    <img
      alt='Quote'
      height='58'
      loading='lazy'
      src='/img/contentful/3sJPqrdR-quote-electric-400.svg'
      width='61'
    />
    <TitleXL as='p' color={BrandColors.primary}>
      {quote.body}
    </TitleXL>
    <TitleSmall as='p' color={BrandColors.pigeon800}>
      {quote.author.name}, {quote.author.title}
    </TitleSmall>
    <img
      alt={`${quote.company.name} logo`}
      className='logo'
      loading='lazy'
      src={quote.company.logoSrc}
    />
  </QuoteCardWrapper>
)

const QuoteCardSectionWrapper = styled(ResponsiveSectionWrapper as any)<{
  bgColorBottom?: string
  bgColorTop?: string
  bgGradientDistance?: string
}>`
  background: linear-gradient(
    to bottom,
    ${BrandColors.white} ${props => props.bgGradientDistance || '10%'},
    ${BrandColors.pigeon100} ${props => props.bgGradientDistance || '10%'}
  );

  ${props =>
    props.bgColorTop && props.bgColorBottom
      ? `background: linear-gradient(to bottom, ${props.bgColorTop} ${
          props.bgGradientDistance || '10%'
        }, ${props.bgColorBottom} ${
          props.bgGradientDistance || '10%'
        }) !important;`
      : ''}
`

export const QuoteCardSection = ({
  bgColorBottom,
  bgColorTop,
  bgGradientDistance,
  children,
  quote = {
    author: {
      name: 'Geoff Roberts',
      title: 'Cofounder'
    },
    body:
      'Literally crying tears of joy after onboarding a customer with ' +
      'Flatfile for the first time. How did someone not build this sooner?',
    company: {
      logoSrc: '/img/contentful/3gHqN1s5-outseta.svg',
      name: 'Outseta'
    }
  },
  scale
}: {
  bgColorBottom?: string
  bgColorTop?: string
  bgGradientDistance?: string
  children?: ReactNode
  quote?: IQuoteProps
  scale?: boolean
}) => {
  return scale ? (
    <QuoteCardSectionWrapper
      backgroundColor={BrandColors.pigeon100}
      bgColorBottom={bgColorBottom}
      bgColorTop={bgColorTop}
      bgGradientDistance={bgGradientDistance}
      paddingLarge='2rem 0'
      style={{
        position: 'relative',
        transform: 'scaleY(0.5) translateY(50%)',
        zIndex: '2'
      }}
    >
      <Container
        style={{
          position: 'relative',
          transform: 'translateY(-50%) scaleY(2)',
          zIndex: 2
        }}
      >
        <QuoteCard quote={quote} />
      </Container>
      {children}
    </QuoteCardSectionWrapper>
  ) : (
    <QuoteCardSectionWrapper
      backgroundColor={BrandColors.pigeon100}
      bgColorBottom={bgColorBottom}
      bgColorTop={bgColorTop}
      bgGradientDistance={bgGradientDistance}
      paddingLarge='2rem 0'
    >
      <Container>
        <QuoteCard quote={quote} />
      </Container>
      {children}
    </QuoteCardSectionWrapper>
  )
}

const QuoteCardMinimalContainer = styled.div`
  max-width: 55ch;
  margin: 0 auto;
  position: relative;

  .quotationMark {
    position: absolute;
    bottom: 4rem;
    right: 0;
  }

  /* @media (min-width: ${Breakpoints.md}px) {
    position: relative;
    top: 50%;
    transform: translateY(-50%);
  } */
`

const QuoteCardMinimalQuote = styled.blockquote`
  position: relative;
  z-index: 2;
  padding: 2rem;
  margin: 0;
  margin-top: 2rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
`

const QuotationMark = ({ color }: { color: string }) => (
  <svg
    fill='none'
    height='116'
    viewBox='0 0 136 116'
    width='136'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M76.2473 106.029C75.3063 105.135 75.1024 103.711 75.7548 102.589L99.9625 60.9464C100.505 60.0124 99.8316 58.8413 98.7513 58.8413H92.0736C88.2049 58.8413 85.0687 55.7051 85.0687 51.8364V7.82329C85.0687 3.95457 88.2049 0.818359 92.0736 0.818359H128.995C132.864 0.818359 136 3.95457 136 7.82328V59.1966C136 65.1596 133.624 70.8768 129.398 75.0835L89.467 114.83C88.3919 115.901 86.6603 115.921 85.5604 114.876L76.2473 106.029ZM1.60545 106.029C0.664358 105.135 0.460534 103.711 1.1129 102.589L25.3206 60.9464C25.8635 60.0124 25.1897 58.8413 24.1094 58.8413H17.4317C13.563 58.8413 10.4268 55.7051 10.4268 51.8364V7.82329C10.4268 3.95457 13.563 0.818359 17.4317 0.818359H54.3531C58.2219 0.818359 61.3581 3.95457 61.3581 7.82328V59.1966C61.3581 65.1596 58.9822 70.8768 54.756 75.0835L14.8251 114.83C13.75 115.901 12.0184 115.921 10.9186 114.876L1.60545 106.029Z'
      fill={BrandColors.pigeon200}
    />
  </svg>
)

export const QuoteCardMinimal = ({
  quote = {
    author: {
      name: '',
      title: ''
    },
    body: '',
    company: {
      logoSrc: '',
      name: ''
    }
  } as IQuoteProps
}) => (
  <QuoteCardMinimalContainer>
    <QuoteCardMinimalQuote>
      <TitleXL as='p'>{quote.body}</TitleXL>
      <TitleSmall as='cite'>
        {quote.author.name}, {quote.author.title}, {quote.company.name}
      </TitleSmall>
      {/* <img
        alt={`${quote.company.name} logo`}
        className='quote-logo'
        src={quote.company.logoSrc}
        style={{ maxWidth: '102px' }}
      /> */}
    </QuoteCardMinimalQuote>

    <div className='quotationMark'>
      <QuotationMark color={BrandColors.midnight600} />
    </div>
  </QuoteCardMinimalContainer>
)

const QuoteCardNewWrapper = styled(ResponsiveSectionWrapper)``

const QuoteCardNewCta = styled.div`
  margin: 3rem auto 0;
  text-align: center;
  display: block;
  max-width: 55ch;
`

const QuoteCardNewInner = styled.div`
  border: 2px solid ${BrandColors.black};
  background-color: ${BrandColors.white};
  box-shadow: 8px 8px 0 -2px #fff, 8px 8px 0 #151515;
  /* min-height: 510px; */
  padding: 1rem;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: ${Breakpoints.sm}px) {
    grid-template-columns: repeat(7, 1fr);
    grid-template-rows: repeat(2, 1fr);

    .photo {
      grid-area: 1 / 5 / 4 / 8;
      position: relative;
    }
    .logo {
      padding: 1rem;
      grid-area: 1 / 1 / 2 / 5;

      img {
        height: 40px;
        width: auto;
      }
    }
    .fullQuote {
      padding: 1rem;
      grid-area: 3 / 1 / 4 / 5;

      p.callout {
        max-width: 24ch;
      }

      @media (min-width: ${Breakpoints.md}px) {
        p {
          max-width: 38ch;
        }
      }
    }
  }
`

const QuoteCardNewImage = styled.img`
  aspect-ratio: 1/1;
  width: 100%;
  vertical-align: middle;
  object-fit: cover;
  height: 100%;
`

const QuoteCardNoImage = styled.div`
  aspect-ratio: 1/1;
  width: 100%;
  vertical-align: middle;
  object-fit: cover;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${BrandColors.pigeon100};
  color: ${BrandColors.black};

  i {
    /* create fluid font size with clamp */
    font-size: clamp(8rem, 15vw, 16rem);
  }
`

// const QuoteCardNewData = styled.div`
//   display: flex;
//   flex-direction: column;
//   justify-content: space-between;
//   gap: 2rem;
//   max-width: 558px;

//   @media (min-width: ${Breakpoints.md}px) {
//     min-height: 510px;
//   }
// `

export const QuoteCardNew = ({
  cta,
  paddingLarge = '6rem 0',
  paddingSmall = '3rem 0',
  quote = {
    author: {
      name: '',
      title: ''
    },
    body: '',
    company: {
      jewelLogoSrc: '',
      logoSrc: '',
      name: '',
      photoSrc: ''
    }
  }
}: {
  cta?: {
    text: string
    url: string
  }
  paddingSmall?: string
  paddingLarge?: string
  quote?: IQuoteProps
}) => (
  <QuoteCardNewWrapper paddingLarge={paddingLarge} paddingSmall={paddingSmall}>
    <Container>
      <QuoteCardNewInner>
        {quote.company.logoSrcBlack && (
          <div className='logo'>
            <img
              alt={`${quote.company?.name} logo`}
              height={40}
              loading='lazy'
              src={quote.company?.logoSrcBlack}
            />
          </div>
        )}

        <div className='fullQuote'>
          {quote?.callout && (
            <TitleH2
              as='p'
              color={BrandColors.primary}
              marginBottom='1.5rem'
              textWrapBalance
              className='callout'
            >
              {quote.callout}
            </TitleH2>
          )}
          <Body2Regular fontSize={1.5} marginBottom='2rem'>
            "{quote.body}"
          </Body2Regular>
          <Body3>
            {quote.author?.name}, {quote.author?.title}
          </Body3>
        </div>

        <div className='photo'>
          {quote.company?.photoSrc ? (
            <QuoteCardNewImage
              alt='Office photo'
              loading='lazy'
              src={quote.company?.photoSrc}
            />
          ) : (
            <QuoteCardNoImage>
              <FaIcon classes='fa-sharp fa-light fa-quotes' size='16rem' />
            </QuoteCardNoImage>
          )}
        </div>
      </QuoteCardNewInner>

      {cta && cta.text && cta.url && (
        <QuoteCardNewCta>
          <StyledLinkLarge
            className='ctaLink'
            color={BrandTokens.linkPrimary}
            hoverColor={BrandTokens.linkPrimaryHover}
            fontWeight={600}
            arrow={true}
            to={cta.url}
          >
            {cta.text}
          </StyledLinkLarge>
        </QuoteCardNewCta>
      )}
    </Container>
  </QuoteCardNewWrapper>
)
